import { AppConfig, PublicAppConfig } from '../types/appConfig';

function normalizeApiBaseUrl(value: string | undefined): string {
  const trimmedValue = (value || '').trim();

  if (!trimmedValue) {
    return '/api';
  }

  if (trimmedValue.startsWith('/')) {
    return trimmedValue.replace(/\/+$/, '') || '/api';
  }

  const normalizedCandidate = /^[a-z]+:\/\//i.test(trimmedValue)
    ? trimmedValue
    : `https://${trimmedValue}`;

  try {
    const url = new URL(normalizedCandidate);
    const normalizedPath =
      !url.pathname || url.pathname === '/'
        ? '/api'
        : url.pathname.replace(/\/+$/, '');

    return `${url.origin}${normalizedPath}`;
  } catch {
    return trimmedValue.replace(/\/+$/, '');
  }
}

const bootstrapApiBaseUrl = normalizeApiBaseUrl(import.meta.env.VITE_API_BASE_URL);

let publicRuntimeConfig: PublicAppConfig | undefined;

async function readPublicConfigResponse(response: Response): Promise<PublicAppConfig> {
  const contentType = response.headers.get('content-type') || '';

  if (!contentType.includes('application/json')) {
    throw new Error('Runtime config endpoint did not return JSON');
  }

  const config = (await response.json()) as PublicAppConfig;

  return {
    ...config,
    frontend: {
      ...config.frontend,
      apiBaseUrl: normalizeApiBaseUrl(config.frontend.apiBaseUrl),
    },
  };
}

function buildFallbackPublicConfig(): PublicAppConfig {
  return {
    frontend: {
      azureClientId: import.meta.env.VITE_AZURE_CLIENT_ID || '',
      azureTenantId: import.meta.env.VITE_AZURE_TENANT_ID || '',
      apiBaseUrl: normalizeApiBaseUrl(bootstrapApiBaseUrl),
    },
    version: import.meta.env.VITE_APP_VERSION || '1.0.0',
  };
}

export async function loadPublicRuntimeConfig(): Promise<PublicAppConfig> {
  try {
    const response = await fetch(`${bootstrapApiBaseUrl}/public-config`);
    if (!response.ok) {
      throw new Error(`HTTP ${response.status}`);
    }

    publicRuntimeConfig = await readPublicConfigResponse(response);
    return publicRuntimeConfig;
  } catch (error) {
    console.warn('Falling back to bootstrap frontend config', error);
    publicRuntimeConfig = buildFallbackPublicConfig();
    return publicRuntimeConfig;
  }
}

export function getPublicRuntimeConfig(): PublicAppConfig {
  return publicRuntimeConfig || buildFallbackPublicConfig();
}

export function getApiBaseUrl(): string {
  return normalizeApiBaseUrl(getPublicRuntimeConfig().frontend.apiBaseUrl || bootstrapApiBaseUrl);
}

export function buildRuntimeAppConfig(): AppConfig {
  const fallback = buildFallbackPublicConfig();

  return {
    backend: {
      azureTenantId: '',
      azureClientId: fallback.frontend.azureClientId,
      azureSubscriptionId: '',
      applicationInsightsConnectionString: '',
    },
    frontend: fallback.frontend,
    resourceGroups: [],
    hostPools: [],
  };
}
